import { z } from "zod"

// Этап 7: «личное дело» продавца, авто-проверка ИНН и бейдж «Проверенный
// продавец» (fd-onboard-05..12, fd-admin-06).

/** Тип документа продавца (fd-onboard-05/06/07). */
export const SellerDocumentTypeSchema = z.enum([
  "passport_ip", // паспорт ИП
  "tax_system", // документ о системе налогообложения
  "authority_ul", // документ о полномочиях/доверенность для ЮЛ
  "other", // прочее
])
export type SellerDocumentType = z.infer<typeof SellerDocumentTypeSchema>

/** Статус модерации документа (fd-onboard-32, fd-admin-06). */
export const SellerDocumentStatusSchema = z.enum([
  "uploaded", // загружен, ждёт проверки модератором
  "accepted", // модератор принял
  "rejected", // модератор отклонил
])
export type SellerDocumentStatus = z.infer<typeof SellerDocumentStatusSchema>

/** Документ «личного дела» продавца. Ключ объекта в приватном бакете наружу не
 *  отдаётся — вместо него ссылка на авторизованный маршрут выдачи файла. */
export const SellerDocumentSchema = z.object({
  id: z.string(),
  docType: SellerDocumentTypeSchema,
  filename: z.string(),
  mime: z.string(),
  sizeBytes: z.number(),
  status: SellerDocumentStatusSchema,
  reviewNote: z.string().optional(),
  reviewedAt: z.string().optional(),
  createdAt: z.string(),
  // Относительный путь к авторизованной выдаче файла.
  fileUrl: z.string(),
  // Автопроверка читаемости (П7, fd-onboard-32): `low` - принят с пометкой,
  // причина в qualityNote. Нет поля - файл загружен до проверки.
  quality: z.enum(["ok", "low", "unreadable"]).optional(),
  qualityNote: z.string().optional(),
})
export type SellerDocument = z.infer<typeof SellerDocumentSchema>

export const SellerDocumentListSchema = z.object({
  documents: z.array(SellerDocumentSchema),
})
export type SellerDocumentList = z.infer<typeof SellerDocumentListSchema>

/** Статус авто-проверки ИНН через DaData (fd-onboard-11).
 *  `unchecked` — мягкая деградация: ключ не задан либо сервис недоступен. */
export const InnCheckStatusSchema = z.enum(["unverified", "verified", "not_found", "unchecked"])
export type InnCheckStatus = z.infer<typeof InnCheckStatusSchema>

/** Результат авто-проверки ИНН продавца. */
export const InnVerificationResultSchema = z.object({
  inn: z.string(),
  status: InnCheckStatusSchema,
  // Наименование из реестра (когда найдено).
  name: z.string().optional(),
  // Тип субъекта: LEGAL / INDIVIDUAL и т.п. из DaData.
  type: z.string().optional(),
  checkedAt: z.string().optional(),
  /** Основной ОКВЭД из реестра, например «47.91». */
  okved: z.string().optional(),
  /** Расшифровка основного ОКВЭД. */
  okvedName: z.string().optional(),
  /**
   * Статус контрагента в реестре: ACTIVE — действующий, остальное (ликвидация,
   * банкротство, реорганизация) площадка обязана увидеть до первой выплаты.
   */
  registryStatus: z.string().optional(),
  /**
   * Предупреждения для менеджера: несовпадение ОКВЭД с торговлей, недействующий
   * контрагент. Продажи НЕ блокируют — решение остаётся за человеком.
   */
  warnings: z.array(z.string()).default([]),
})
export type InnVerificationResult = z.infer<typeof InnVerificationResultSchema>

/** Три состояния документа для наглядной сводки «личного дела»:
 *  missing (не загружен) → uploaded (на проверке) / rejected → accepted (принят).
 *  Выводится и в кабинете продавца, и в админке. */
export const SellerDocumentChecklistStatusSchema = z.enum([
  "missing", // документ этого типа не загружен
  "uploaded", // загружен, ждёт проверки модератором
  "rejected", // модератор отклонил (нужна перезагрузка)
  "accepted", // модератор принял
])
export type SellerDocumentChecklistStatus = z.infer<typeof SellerDocumentChecklistStatusSchema>

/** Строка чек-листа обязательных документов «личного дела». */
export const SellerDocumentChecklistItemSchema = z.object({
  docType: SellerDocumentTypeSchema,
  status: SellerDocumentChecklistStatusSchema,
})
export type SellerDocumentChecklistItem = z.infer<typeof SellerDocumentChecklistItemSchema>

/** Полное состояние верификации продавца, включая итоговый бейдж
 *  «Проверенный продавец» (fd-onboard-12). */
export const SellerVerificationSchema = z.object({
  inn: InnVerificationResultSchema.nullable(),
  // Хотя бы один документ «личного дела» принят модератором.
  documentsAccepted: z.boolean(),
  // Чек-лист обязательных документов с тремя состояниями (missing → uploaded /
  // rejected → accepted) — наглядная сводка для кабинета продавца и админки.
  checklist: z.array(SellerDocumentChecklistItemSchema),
  // Итоговый «зелёный флаг»: бейдж выдан (документы приняты + ИНН проверен +
  // продавец активен).
  verified: z.boolean(),
  verifiedAt: z.string().nullable(),
})
export type SellerVerification = z.infer<typeof SellerVerificationSchema>
